<template>
  <div class="mall-prescription">
    <div class="page">
      <MallHeader @back-click="goBack">
        <div slot="right"></div>
      </MallHeader>

      <div class="mall-prescription__content">
        <div class="mall-prescription__top">
          <Mall3dIcon :src="fileIcon" :size="52" :screenOpacity="0.2" />
          <div class="mall-prescription__title-area">
            <div class="mall-prescription__title">
              <span class="mall-prescription__heading">처방전을<br />제출해 주세요</span>
              <span class="mall-prescription__desc">20MB 이하, 이미지 혹은 PDF만 가능해요.</span>
            </div>
            <div class="mall-prescription__upload">
              <MallButton
                :label="'파일 업로드'"
                :type="'secondary'"
                :size="'sm'"
                :icon="clipIcon"
                :disabled="isRequesting"
                @click="openFilePicker"
              />
            </div>
            <input
              ref="fileInput"
              class="mall-prescription__file-input"
              type="file"
              accept="image/*,application/pdf,.pdf"
              multiple
              @change="onFileChange"
            />
          </div>
        </div>

        <MallPrescriptionFiles
          v-if="files.length > 0"
          :files="files"
          @remove="removeFile"
        />
      </div>

      <MallBottomBar :buttons="buttons" @click="onButtonClick" />

      <MallToast
        v-if="toast"
        :key="toast.key"
        :title="toast.title"
        :message="toast.message"
        @close="toast = null"
      />

      <!-- 처방전이 없을 때 -->
      <MallConfirmPopup
        v-if="isNoPrescriptionPopup"
        :icon="warningIcon"
        :title="'위임청구로 구매하려면\n처방전이 필요해요'"
        :messageLines="['진료 후 처방전을 받아 다시 구매하거나,', '위임청구 없이 바로 구매할 수 있어요.']"
        :cancelLabel="'진료 받기'"
        :confirmLabel="'바로 구매'"
        @close="isNoPrescriptionPopup = false"
        @cancel="goBooking"
        @confirm="buyWithoutPrescription"
      />
    </div>
  </div>
</template>

<script>
import Constant from '@/constant'
import mall from '@/utilities/mall.js'
import Mall3dIcon from '@/components/new/atoms/Mall3dIcon.vue'
import MallButton from '@/components/new/atoms/MallButton.vue'
import MallHeader from '@/components/new/molecules/MallHeader.vue'
import MallBottomBar from '@/components/new/molecules/MallBottomBar.vue'
import MallConfirmPopup from '@/components/new/molecules/MallConfirmPopup.vue'
import MallToast from '@/components/new/molecules/MallToast.vue'
import MallPrescriptionFiles from '@/components/new/organisms/MallPrescriptionFiles.vue'

var MAX_FILES = 3
var MAX_FILE_SIZE = 20 * 1024 * 1024

export default {
  name: 'MallPrescription',
  components: {
    Mall3dIcon,
    MallButton,
    MallHeader,
    MallBottomBar,
    MallConfirmPopup,
    MallToast,
    MallPrescriptionFiles,
  },
  data() {
    return {
      profileId: Number(this.$route.query.profileId) || null,
      // 장바구니에서 위임청구를 해제했으면 false (구매하기 화면 결제 준비에 전달)
      delegationClaim: this.$route.query.delegationClaim !== 'false',
      token: null,
      fileIcon: require('@/assets/mall/img_file_3d.png'),
      clipIcon: require('@/assets/mall/icn_clip_line.svg'),
      warningIcon: require('@/assets/mall/img_warning_triangle_3d.png'),
      // 장바구니에 첨부된 처방전 [{ id, name, isPdf, previewUrl }]
      files: [],
      // 업로드 / 삭제 중에는 버튼 잠금
      isRequesting: false,
      toast: null,
      isNoPrescriptionPopup: false,
    }
  },
  computed: {
    buttons() {
      return [
        { key: 'none', label: '없어요', type: 'soft', disabled: this.isRequesting },
        { key: 'confirm', label: '확인', type: 'primary', disabled: this.files.length === 0 || this.isRequesting },
      ]
    },
  },
  mounted() {
    ChannelIO('shutdown')
    this.token = mall.getAccessToken()
    if (!this.token) {
      mall.goLogin(this.$router)
      return
    }
    this.fetchFiles().catch((err) => this.handleError(err))
  },
  methods: {
    goBack() {
      mall.goBack(this.$router, this.$route, { path: '/mall/cart', query: { profileId: this.profileId } })
    },
    // 장바구니 첨부 파일 조회 (등록 API가 id를 주지 않아 업로드 후에도 다시 조회)
    fetchFiles() {
      return this.$store.dispatch(Constant.FETCH_CART, { profile_id: this.profileId, token: this.token })
        .then((cart) => {
          this.files = ((cart && cart.files) || []).map((file) => {
            var isPdf = file.content_type === 'application/pdf' || /\.pdf$/i.test(file.name)
            return { id: file.id, name: file.name, isPdf: isPdf, previewUrl: isPdf ? '' : file.url }
          })
        })
    },
    openFilePicker() {
      if (this.isRequesting) return
      if (this.files.length >= MAX_FILES) {
        this.showMaxToast()
        return
      }
      this.$refs.fileInput.click()
    },
    onFileChange(event) {
      var selected = Array.prototype.slice.call(event.target.files || [])
      // 같은 파일을 다시 고를 수 있도록 초기화
      event.target.value = ''

      var valid = selected.filter(file => this.isAllowedFile(file))
      var remaining = MAX_FILES - this.files.length
      var uploads = valid.slice(0, remaining)
      var showResultToast = () => {
        if (valid.length > remaining) {
          this.showMaxToast()
        } else if (valid.length < selected.length) {
          this.showToast('업로드할 수 없는 파일이 있어요.', '20MB 이하, 이미지 혹은 PDF만 가능해요.')
        }
      }
      if (uploads.length === 0) {
        showResultToast()
        return
      }

      this.isRequesting = true
      this.$store.dispatch(Constant.UPLOAD_CART_FILES, { profile_id: this.profileId, files: uploads, token: this.token })
        .then(() => this.fetchFiles())
        .then(() => showResultToast())
        .catch((err) => this.handleError(err))
        .then(() => {
          this.isRequesting = false
        })
    },
    isPdf(file) {
      return file.type === 'application/pdf' || /\.pdf$/i.test(file.name)
    },
    isAllowedFile(file) {
      var isImage = /^image\//.test(file.type)
      return (isImage || this.isPdf(file)) && file.size <= MAX_FILE_SIZE
    },
    removeFile(id) {
      if (this.isRequesting) return
      this.isRequesting = true
      this.$store.dispatch(Constant.DELETE_CART_FILE, { file_id: id, profile_id: this.profileId, token: this.token })
        .then(() => {
          this.files = this.files.filter(file => file.id !== id)
        })
        .catch((err) => this.handleError(err))
        .then(() => {
          this.isRequesting = false
        })
    },
    handleError(err) {
      if (mall.isAuthError(err)) {
        mall.goLogin(this.$router)
        return
      }
      this.showToast('요청을 처리하지 못했어요.', mall.errorMessage(err))
    },
    showMaxToast() {
      this.showToast('최대 3개까지 업로드할 수 있어요.', '첨부된 파일 개수를 확인해 주세요.')
    },
    showToast(title, message) {
      this.toast = { key: Date.now(), title: title, message: message }
    },
    onButtonClick(key) {
      if (this.isRequesting) return
      if (key === 'none') {
        this.isNoPrescriptionPopup = true
        return
      }
      // 처방전은 장바구니 첨부로 등록돼 있고 결제 준비(checkout) 때 진료 파일로 옮겨짐
      this.goOrder(this.delegationClaim)
    },
    goBooking() {
      this.$router.push({ path: '/mall/booking', query: { profileId: this.profileId } })
    },
    // 처방전 없이 바로 구매 = 위임청구 없이 전액 본인부담
    buyWithoutPrescription() {
      this.goOrder(false)
    },
    // 구매하기에서 뒤로가면 이 화면(위임청구 해제 여부 포함)으로 돌아오도록 returnTo 전달
    goOrder(delegationClaim) {
      this.$router.push({
        path: '/mall/order',
        query: { profileId: this.profileId, delegationClaim: delegationClaim ? undefined : 'false', returnTo: this.$route.fullPath },
      })
    },
  },
}
</script>

<style scoped>
  .mall-prescription {
    display: flex;
    width: 100%;
    min-height: 100vh;
    justify-content: center;
    align-items: flex-start;
  }
  /* 하단 고정 버튼 영역(90px)에 내용이 가려지지 않도록 */
  .page {
    width: 100%;
    min-height: 100vh;
    padding-bottom: 90px;
    background: #FFF;
    box-sizing: border-box;
  }
  .mall-prescription__content {
    display: flex;
    padding: 4px 16px 32px 16px;
    flex-direction: column;
    gap: 48px;
  }
  .mall-prescription__top {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .mall-prescription__title-area {
    display: flex;
    padding-left: 4px;
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  }
  .mall-prescription__title {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .mall-prescription__heading {
    color: var(--color-text-main);
    font-family: Pretendard;
    font-size: var(--font-size-2xl);
    font-weight: var(--font-weight-semibold);
    line-height: var(--line-height-tight);
  }
  .mall-prescription__desc {
    color: #6B7684;
    font-family: Pretendard;
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    line-height: var(--line-height-normal);
  }
  .mall-prescription__upload {
    display: flex;
  }
  /* Figma 기준 고정 폭 93px (아이콘 + 글자 가운데 정렬) */
  .mall-prescription__upload .mall-button {
    width: 93px;
    padding: 0;
  }
  .mall-prescription__upload .mall-button--disabled {
    opacity: 0.5;
    cursor: default;
  }
  .mall-prescription__file-input {
    display: none;
  }
  @media (min-width: 600px) {
    .page {
      width: 600px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/page/new/MallPrescription.vue